import { FC, memo, useCallback, useEffect, useState } from 'react';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Stack, Tooltip } from '@mui/material';

import {
  ArtworkThumbnail,
  MoreArtworkButton,
  NonImageFileThumbnail,
} from '@/features/media/ui/TableCellFilesPreview/TableCellFilesPreview.styles';
import { isNonImageFile, isVideoFile } from '@/shared/lib/files';
import {
  ImageResource,
  resourceLeafId,
  usePrefetchResource,
  useResourceImage,
} from '@/shared/lib/hooks/useResourceImage';
import ImagesLightbox from '@/shared/ui/ImagesLightbox';
import { ResourceImage } from '@/shared/ui/ResourceImage';

export interface ResourceImageGalleryItem {
  resource: ImageResource;
  name?: string | null;
}

interface GalleryThumbnailProps {
  item: ResourceImageGalleryItem;
  onClick: () => void;
}

const GalleryThumbnail = memo(function GalleryThumbnail({ item, onClick }: GalleryThumbnailProps) {
  const [previewMounted, setPreviewMounted] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const { url: thumbUrl } = useResourceImage(item.resource, { size: { tier: 'thumb' } });

  useEffect(() => {
    setImgLoaded(false);
  }, [thumbUrl]);

  if (isVideoFile(item.name)) {
    return (
      <Tooltip placement="top" title={item.name ?? ''}>
        <NonImageFileThumbnail onClick={onClick} data-testid="resource-thumbnail-video">
          <VideocamIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
        </NonImageFileThumbnail>
      </Tooltip>
    );
  }

  if (isNonImageFile(item.name)) {
    return (
      <Tooltip placement="top" title={item.name ?? ''}>
        <NonImageFileThumbnail onClick={onClick} data-testid="resource-thumbnail-non-image">
          <PictureAsPdfIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
        </NonImageFileThumbnail>
      </Tooltip>
    );
  }

  if (!thumbUrl) {
    return (
      <ArtworkThumbnail as="div" onClick={onClick} data-testid="resource-thumbnail-placeholder" />
    );
  }

  return (
    <Tooltip
      placement="top"
      enterDelay={150}
      onOpen={() => setPreviewMounted(true)}
      title={
        previewMounted ? (
          <ResourceImage
            resource={item.resource}
            size={{ tier: 'preview' }}
            alt={item.name ?? 'preview'}
            placeholder="none"
            fadeIn={false}
            objectFit="contain"
            sx={{ maxWidth: '12.5rem', maxHeight: '12.5rem' }}
          />
        ) : null
      }
    >
      <ArtworkThumbnail
        src={thumbUrl}
        alt={item.name ?? ''}
        onClick={onClick}
        onLoad={() => setImgLoaded(true)}
        data-testid="resource-thumbnail"
        style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 120ms ease-out' }}
      />
    </Tooltip>
  );
});

interface Props {
  items: ResourceImageGalleryItem[];
  max?: number;
  onThumbnailClick?: (index: number) => void;
  onMoreClick?: (index: number) => void;
  disableLightbox?: boolean;
}

const ResourceImageGallery: FC<Props> = ({
  items,
  max = 5,
  onThumbnailClick,
  onMoreClick,
  disableLightbox,
}) => {
  const prefetch = usePrefetchResource();
  const [lightboxFiles, setLightboxFiles] = useState<File[]>([]);
  const [startingIndex, setStartingIndex] = useState<number>();

  const openLightbox = useCallback(
    async (index: number) => {
      const downloaded = await Promise.all(
        items.map(async (item) => {
          try {
            const blob = await prefetch(item.resource);
            return new File([blob], item.name ?? '');
          } catch (error) {
            console.error('Failed to download resource image:', item.name, error);
            return null;
          }
        }),
      );
      setLightboxFiles(downloaded.filter((f): f is File => f != null));
      setStartingIndex(index);
    },
    [items, prefetch],
  );

  const handleClick = useCallback(
    (index: number) => {
      if (onThumbnailClick) {
        onThumbnailClick(index);
        return;
      }
      if (disableLightbox) return;
      openLightbox(index);
    },
    [onThumbnailClick, disableLightbox, openLightbox],
  );

  const handleMoreClick = useCallback(() => {
    if (onMoreClick) {
      onMoreClick(max);
      return;
    }
    if (disableLightbox) return;
    openLightbox(max);
  }, [onMoreClick, disableLightbox, openLightbox, max]);

  if (!items.length) return null;

  const visible = items.slice(0, max);
  const overflow = items.length - max;

  return (
    <>
      <Stack direction="row" alignItems="center" ml="0.125rem">
        {visible.map((item, index) => (
          <GalleryThumbnail
            key={`${item.resource.kind}-${resourceLeafId(item.resource)}-${index}`}
            item={item}
            onClick={() => handleClick(index)}
          />
        ))}
        {overflow > 0 && (
          <MoreArtworkButton onClick={handleMoreClick} data-testid="resource-more">
            +{overflow}
          </MoreArtworkButton>
        )}
      </Stack>

      {!disableLightbox && !!lightboxFiles.length && (
        <ImagesLightbox
          open={startingIndex !== undefined}
          onClose={() => setStartingIndex(undefined)}
          images={lightboxFiles}
          startingIndex={startingIndex}
        />
      )}
    </>
  );
};

export default ResourceImageGallery;
